import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminEntity,
  deleteAdminEntity,
  getAdminEntityList,
  updateAdminEntity,
} from "../../api/admin.api";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import AdminLayout from "../../layouts/AdminLayout";
import AdminBlockingModal from "./AdminBlockingModal";
import { getPlatformRole, isAdminRole } from "../../utils/platformRole";
import { handleTableKeyNav } from "../../utils/tableNav";
import normalizeApiError from "../../utils/normalizeApiError";
import SuccessToast from "../../components/ui/SuccessToast";
import useSingleFlight from "../../utils/useSingleFlight";

const APPROVED_ENTITIES = [
  "vastu_direction",
  "vastu_element",
  "vastu_rule",
  "plan_type",
  "credit_pack",
  "report_template",
  "ui_layout",
  "notification_type",
  "feature_flag",
  "payment_mode",
];

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const getItemField = (item, keys) => {
  for (const key of keys) {
    if (item?.[key] != null) return item[key];
  }
  const data = item?.data;
  if (data && typeof data === "object") {
    for (const key of keys) {
      if (data?.[key] != null) return data[key];
    }
  }
  return null;
};

const resolveStatus = (item) => {
  const status =
    getItemField(item, ["status", "state"]) ?? getItemField(item, ["active"]);
  if (typeof status === "boolean") {
    return status ? "Active" : "Inactive";
  }
  if (!status) return "—";
  return String(status);
};

const resolveStatusTone = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "active") return "bg-emerald-100 text-emerald-700";
  if (value === "inactive" || value === "disabled")
    return "bg-slate-100 text-slate-500";
  if (value === "pending") return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-500";
};

export default function AdminMasterData() {
  const role = getPlatformRole();
  const [entity, setEntity] = useState(APPROVED_ENTITIES[0]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blockingError, setBlockingError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionLogs, setActionLogs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const createTriggerRef = React.useRef(null);
  const updateTriggerRef = React.useRef(null);
  const deleteTriggerRef = React.useRef(null);
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [createForm, setCreateForm] = useState({
    code: "",
    label: "",
    active: true,
    description: "",
  });
  const [updateId, setUpdateId] = useState("");
  const [updateForm, setUpdateForm] = useState({
    label: "",
    active: true,
    description: "",
  });
  const [deleteId, setDeleteId] = useState("");
  const createFlight = useSingleFlight();
  const updateFlight = useSingleFlight();
  const deleteFlight = useSingleFlight();

  const canGoPrev = page > 1;
  const canGoNext = total ? page * pageSize < total : items.length === pageSize;

  const loadList = async (nextPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAdminEntityList(entity, {
        page: nextPage,
        pageSize,
      });
      setItems(Array.isArray(response?.items) ? response.items : []);
      setPage(response?.page || nextPage);
      setTotal(response?.total ?? null);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "INVALID_ENTITY") {
        setError(
          normalizeApiError({
            code: "INVALID_ENTITY",
            message: "Invalid entity selection.",
          })
        );
        return;
      }
      if (code === "BILLING_CREDITS_EXHAUSTED") {
        setBlockingError(err?.response?.data?.message || "Credits exhausted.");
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList(1);
  }, [entity]);

  const canManage = isAdminRole(role);

  const resetActionMessages = () => {
    setError(null);
    setActionMessage("");
  };

  const closeCreateModal = () => {
    setShowCreate(false);
    createTriggerRef.current?.focus();
  };

  const closeUpdateModal = () => {
    setShowUpdate(false);
    updateTriggerRef.current?.focus();
  };

  const closeDeleteModal = () => {
    setShowDelete(false);
    deleteTriggerRef.current?.focus();
  };

  const logAction = (entry) => {
    setActionLogs((prev) => [entry, ...prev].slice(0, 10));
  };

  const handleCreate = async () =>
    createFlight.run(async () => {
    if (!canManage) return;
    resetActionMessages();
    setLoading(true);
    const payload = {
      code: createForm.code?.trim(),
      label: createForm.label?.trim(),
      active: Boolean(createForm.active),
      description: createForm.description?.trim() || undefined,
    };
    if (!payload.code || !payload.label) {
      setError(normalizeApiError("Create requires code and label."));
      setLoading(false);
      return;
    }
    try {
      await createAdminEntity(entity, payload);
      logAction({
        action: "create",
        entity,
        id: payload.code,
        status: "success",
        apiCalls: 1,
      });
      setActionMessage("Create succeeded.");
      setShowCreate(false);
      setCreateForm({ code: "", label: "", active: true, description: "" });
      await loadList(1);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "INVALID_ENTITY") {
        setError(
          normalizeApiError({
            code: "INVALID_ENTITY",
            message: "Invalid entity selection.",
          })
        );
      } else if (code === "BILLING_CREDITS_EXHAUSTED") {
        setBlockingError(err?.response?.data?.message || "Credits exhausted.");
      } else {
        setError(normalizeApiError(err));
      }
      logAction({
        action: "create",
        entity,
        id: payload.code,
        status: "error",
        apiCalls: 1,
      });
    } finally {
      setLoading(false);
    }
    });

  const handleUpdate = async () =>
    updateFlight.run(async () => {
    if (!canManage) return;
    resetActionMessages();
    setLoading(true);
    const payload = {
      label: updateForm.label?.trim(),
      active: Boolean(updateForm.active),
      description: updateForm.description?.trim() || undefined,
    };
    if (!updateId) {
      setError(normalizeApiError("Update requires an ID."));
      setLoading(false);
      return;
    }
    if (!payload.label && !payload.description) {
      setError(normalizeApiError("Update requires at least one field."));
      setLoading(false);
      return;
    }
    if (!confirmUpdate) {
      setError(normalizeApiError("Please confirm the update."));
      setLoading(false);
      return;
    }
    try {
      await updateAdminEntity(entity, updateId, payload);
      logAction({
        action: "update",
        entity,
        id: updateId,
        status: "success",
        apiCalls: 1,
      });
      setActionMessage("Update succeeded.");
      setShowUpdate(false);
      setConfirmUpdate(false);
      await loadList(page);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "INVALID_ENTITY") {
        setError(
          normalizeApiError({
            code: "INVALID_ENTITY",
            message: "Invalid entity selection.",
          })
        );
      } else {
        setError(normalizeApiError(err));
      }
      logAction({
        action: "update",
        entity,
        id: updateId,
        status: "error",
        apiCalls: 1,
      });
    } finally {
      setLoading(false);
    }
    });

  const handleDelete = async () =>
    deleteFlight.run(async () => {
    if (!canManage) return;
    resetActionMessages();
    setLoading(true);
    if (!deleteId) {
      setError(normalizeApiError("Delete requires an ID."));
      setLoading(false);
      return;
    }
    if (!confirmDelete) {
      setError(normalizeApiError("Please confirm the deletion."));
      setLoading(false);
      return;
    }
    try {
      await deleteAdminEntity(entity, deleteId);
      logAction({
        action: "delete",
        entity,
        id: deleteId,
        status: "success",
        apiCalls: 1,
      });
      setActionMessage("Delete succeeded.");
      setShowDelete(false);
      setConfirmDelete(false);
      await loadList(page);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "INVALID_ENTITY") {
        setError(
          normalizeApiError({
            code: "INVALID_ENTITY",
            message: "Invalid entity selection.",
          })
        );
      } else {
        setError(normalizeApiError(err));
      }
      logAction({
        action: "delete",
        entity,
        id: deleteId,
        status: "error",
        apiCalls: 1,
      });
    } finally {
      setLoading(false);
    }
    });

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const id = String(getItemField(item, ["id", "_id"]) || "");
      const name = String(
        getItemField(item, ["name", "title", "label", "code"]) || ""
      );
      const status = String(resolveStatus(item) || "");
      return (
        id.toLowerCase().includes(query) ||
        name.toLowerCase().includes(query) ||
        status.toLowerCase().includes(query)
      );
    });
  }, [items, search]);

  const listMeta = useMemo(() => {
    if (total == null) return "Pagination info unavailable.";
    return `Page ${page} of ${Math.ceil(total / pageSize)}`;
  }, [page, pageSize, total]);

  return (
    <AdminLayout title="Admin Master Data">
      <div className="space-y-6">
        <div className="card p-4">
          <AdminBreadcrumbs />
          <label className="text-xs text-slate-500">Entity</label>
          <select
            className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
            value={entity}
            onChange={(event) => setEntity(event.target.value)}
          >
            {APPROVED_ENTITIES.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <button className="btn btn-primary mt-3" onClick={() => loadList(1)}>
            Load
          </button>
          <p className="mt-2 text-xs text-slate-500">{listMeta}</p>
          {error && (
            <div className="mt-3 text-xs text-rose-600">{error.message}</div>
          )}
          {actionMessage && (
            <div className="mt-3">
              <SuccessToast
                message={actionMessage}
                onDismiss={() => setActionMessage("")}
                testId="admin-master-success"
              />
            </div>
          )}
        </div>

        <div className="card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="btn btn-primary"
              onClick={() => setShowCreate(true)}
              disabled={!canManage || loading}
              title={canManage ? "Create new record" : "Not authorized"}
              ref={createTriggerRef}
            >
              Create
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowUpdate(true)}
              disabled={!canManage || loading}
              title={canManage ? "Update existing record" : "Not authorized"}
              ref={updateTriggerRef}
            >
              Update
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowDelete(true)}
              disabled={!canManage || loading}
              title={canManage ? "Delete record" : "Not authorized"}
              ref={deleteTriggerRef}
            >
              Delete
            </button>
            {!canManage && (
              <span className="text-xs text-rose-600">Not authorized.</span>
            )}
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">Records</p>
            <div className="flex gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => loadList(page - 1)}
                disabled={!canGoPrev || loading}
              >
                Prev
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => loadList(page + 1)}
                disabled={!canGoNext || loading}
              >
                Next
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <input
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm md:max-w-xs"
              placeholder="Search by ID, name, or status"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <span className="text-xs text-slate-500">
              Showing {filteredItems.length} of {items.length} records
            </span>
          </div>
          {loading ? (
            <LoadingState variant="table" rows={5} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => loadList(page)} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No master records found"
              message="This master table has no records yet."
              testId="admin-empty-master-data"
            />
          ) : (
            <>
              <div
                className="mt-4 hidden overflow-hidden rounded-lg border border-slate-200 lg:block"
                onKeyDown={handleTableKeyNav}
              >
                <table
                  className="w-full text-left text-sm"
                  role="table"
                  aria-label="Master data records"
                >
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3" title="Unique record identifier">
                        ID
                      </th>
                      <th className="px-4 py-3" title="Name or label of the record">
                        Name/Title
                      </th>
                      <th className="px-4 py-3" title="Creation timestamp">
                        Created At
                      </th>
                      <th className="px-4 py-3" title="Last update timestamp">
                        Updated At
                      </th>
                      <th className="px-4 py-3" title="Status or activity flag">
                        Status
                      </th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => {
                      const id =
                        getItemField(item, ["id", "_id"]) || item?.id || "—";
                      const name =
                        getItemField(item, ["name", "title", "label", "code"]) ||
                        item?.entity ||
                        entity;
                      const status = resolveStatus(item);
                      const activeValue = getItemField(item, ["active"]);
                      const description = getItemField(item, ["description"]);
                      return (
                        <tr
                          key={id || JSON.stringify(item)}
                          className="border-t border-slate-100 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-400"
                          tabIndex={0}
                          data-row="true"
                        >
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {id}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {name}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {formatTimestamp(item?.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {formatTimestamp(item?.updatedAt)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2 py-1 text-xs font-semibold ${resolveStatusTone(
                                status
                              )}`}
                              title="Status indicator"
                            >
                              {status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-400">
                            <div className="flex items-center gap-2">
                              <button
                                className="text-xs text-amber-600"
                                onClick={() => {
                                  if (!canManage) return;
                                  setUpdateId(String(id));
                                  setUpdateForm({
                                    label: String(
                                      getItemField(item, [
                                        "label",
                                        "name",
                                        "title",
                                        "code",
                                      ]) || ""
                                    ),
                                    active:
                                      typeof activeValue === "boolean"
                                        ? activeValue
                                        : true,
                                    description: String(description || ""),
                                  });
                                  setShowUpdate(true);
                                }}
                                disabled={!canManage}
                                title={canManage ? "Edit record" : "Not authorized"}
                              >
                                Edit
                              </button>
                              <button
                                className="text-xs text-rose-600"
                                onClick={() => {
                                  if (!canManage) return;
                                  setDeleteId(String(id));
                                  setShowDelete(true);
                                }}
                                disabled={!canManage}
                                title={canManage ? "Delete record" : "Not authorized"}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 space-y-3 lg:hidden">
                {filteredItems.map((item) => {
                  const id =
                    getItemField(item, ["id", "_id"]) || item?.id || "—";
                  const name =
                    getItemField(item, ["name", "title", "label", "code"]) ||
                    item?.entity ||
                    entity;
                  const status = resolveStatus(item);
                  const activeValue = getItemField(item, ["active"]);
                  const description = getItemField(item, ["description"]);
                  return (
                    <div
                      key={id || JSON.stringify(item)}
                      className="card p-4 text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-slate-500">Name/Title</p>
                          <p className="font-semibold text-slate-900">{name}</p>
                        </div>
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${resolveStatusTone(
                            status
                          )}`}
                        >
                          {status}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-slate-600">
                        <div>
                          <p className="text-slate-400">ID</p>
                          <p>{id}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Created</p>
                          <p>{formatTimestamp(item?.createdAt)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Updated</p>
                          <p>{formatTimestamp(item?.updatedAt)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Action</p>
                          <div className="flex items-center gap-2">
                            <button
                              className="text-xs text-amber-600"
                              onClick={() => {
                                if (!canManage) return;
                                setUpdateId(String(id));
                                setUpdateForm({
                                  label: String(
                                    getItemField(item, [
                                      "label",
                                      "name",
                                      "title",
                                      "code",
                                    ]) || ""
                                  ),
                                  active:
                                    typeof activeValue === "boolean"
                                      ? activeValue
                                      : true,
                                  description: String(description || ""),
                                });
                                setShowUpdate(true);
                              }}
                              disabled={!canManage}
                              title={canManage ? "Edit record" : "Not authorized"}
                            >
                              Edit
                            </button>
                            <button
                              className="text-xs text-rose-600"
                              onClick={() => {
                                if (!canManage) return;
                                setDeleteId(String(id));
                                setShowDelete(true);
                              }}
                              disabled={!canManage}
                              title={canManage ? "Delete record" : "Not authorized"}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {actionLogs.length > 0 && (
          <div className="card p-4">
            <p className="text-sm font-semibold text-slate-900">Action Log</p>
            <div className="mt-3 space-y-2 text-xs text-slate-600">
              {actionLogs.map((log, index) => (
                <div key={`${log.action}-${log.id}-${index}`}>
                  {log.action.toUpperCase()} {log.entity} ({log.id || "n/a"}) →
                  {` ${log.status}`} (apiCalls: {log.apiCalls})
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AdminBlockingModal
        title="Billing credits exhausted"
        message={blockingError}
        onClose={() => setBlockingError("")}
      />

      {showCreate && (
        <div className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center p-4">
          <div
            className="card modal-panel w-full max-w-lg p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-master-create-title"
          >
            <p
              id="admin-master-create-title"
              className="text-sm font-semibold text-slate-900"
            >
              Create record
            </p>
            <div className="mt-4 space-y-3">
              <input
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Code (required)"
                value={createForm.code}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, code: event.target.value }))
                }
              />
              <input
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Label (required)"
                value={createForm.label}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, label: event.target.value }))
                }
              />
              <input
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Description (optional)"
                value={createForm.description}
                onChange={(event) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={createForm.active}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      active: event.target.checked,
                    }))
                  }
                />
                Active
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="btn btn-secondary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={closeCreateModal}
                disabled={loading || createFlight.isRunning}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleCreate}
                disabled={loading || !canManage || createFlight.isRunning}
                data-testid="admin-master-create-btn"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpdate && (
        <div className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center p-4">
          <div
            className="card modal-panel w-full max-w-lg p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-master-update-title"
          >
            <p
              id="admin-master-update-title"
              className="text-sm font-semibold text-slate-900"
            >
              Update record
            </p>
            <div className="mt-4 space-y-3">
              <input
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Record ID (required)"
                value={updateId}
                onChange={(event) => setUpdateId(event.target.value)}
              />
              <input
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Label"
                value={updateForm.label}
                onChange={(event) =>
                  setUpdateForm((prev) => ({ ...prev, label: event.target.value }))
                }
              />
              <input
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Description (optional)"
                value={updateForm.description}
                onChange={(event) =>
                  setUpdateForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={updateForm.active}
                  onChange={(event) =>
                    setUpdateForm((prev) => ({
                      ...prev,
                      active: event.target.checked,
                    }))
                  }
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={confirmUpdate}
                  onChange={(event) => setConfirmUpdate(event.target.checked)}
                />
                Confirm update
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="btn btn-secondary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={closeUpdateModal}
                disabled={loading || updateFlight.isRunning}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleUpdate}
                disabled={loading || !canManage || updateFlight.isRunning}
                data-testid="admin-master-update-btn"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center p-4">
          <div
            className="card modal-panel w-full max-w-lg p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-master-delete-title"
          >
            <p
              id="admin-master-delete-title"
              className="text-sm font-semibold text-slate-900"
            >
              Delete record
            </p>
            <div className="mt-4 space-y-3">
              <input
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Record ID (required)"
                value={deleteId}
                onChange={(event) => setDeleteId(event.target.value)}
              />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={confirmDelete}
                  onChange={(event) => setConfirmDelete(event.target.checked)}
                />
                Confirm deletion
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                className="btn btn-secondary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={closeDeleteModal}
                disabled={loading || deleteFlight.isRunning}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleDelete}
                disabled={loading || !canManage || deleteFlight.isRunning}
                data-testid="admin-master-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
